import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Plus,
  Search,
  Download, FileDown,
  Upload,
  MoreHorizontal,
  Trash2,
  UserPlus,
  RefreshCcw,
  ChevronLeft,
  ChevronRight,
  Edit,
  Copy,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import LeadDialog from '../components/dialogs/LeadDialog';
import LeadDetailsDialog from '../components/dialogs/LeadDetailsDialog';
import BulkAssignDialog from '../components/dialogs/BulkAssignDialog';
import UploadLeadsDialog from '../components/dialogs/UploadLeadsDialog';
import EditLeadDialog from '../components/dialogs/EditLeadDialog';

const LEAD_STATUSES = [
  'New Lead',
  'Uncontacted',
  'Qualified Lead',
  'Virtual Meet',
  'Awaited Visit',
  'Awaited LOI',
  'Closed LOI',
  'Outlet Opened',
  'Nurture',
  'Dead',
];

const LEAD_SOURCES = ['Manual', 'Bulk Upload', 'Meta Campaign', 'Website', 'Referral'];

const getStatusColor = (status) => {
  const colors = {
    'New Lead': 'bg-blue-100 text-blue-800',
    'Uncontacted': 'bg-gray-100 text-gray-800',
    'Qualified Lead': 'bg-indigo-100 text-indigo-800',
    'Virtual Meet': 'bg-purple-100 text-purple-800',
    'Awaited Visit': 'bg-orange-100 text-orange-800',
    'Awaited LOI': 'bg-yellow-100 text-yellow-800',
    'Nurture': 'bg-teal-100 text-teal-800',
    'Closed LOI': 'bg-teal-100 text-teal-800',
    'Outlet Opened': 'bg-green-100 text-green-800',
    'Dead': 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const AllLeadsPage = () => {
  const { api } = useAuth();
  const [leads, setLeads] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDuplicatesDialog, setShowDuplicatesDialog] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshIntervalRef = useRef(null);

  const fetchLeads = useCallback(async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) setIsRefreshing(true);
      
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (sourceFilter && sourceFilter !== 'all') params.append('source', sourceFilter);
      if (assigneeFilter && assigneeFilter !== 'all') params.append('assignee', assigneeFilter);
      if (assignmentFilter && assignmentFilter !== 'all') params.append('assignment', assignmentFilter);

      const response = await api.get(`/leads?${params.toString()}`);
      setLeads(response.data.leads || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (err) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [api, page, searchTerm, statusFilter, sourceFilter, assigneeFilter, assignmentFilter]);

  const fetchExecutives = useCallback(async () => {
    try {
      const response = await api.get('/users/executives');
      setExecutives(response.data || []);
    } catch (err) {
      console.error('Failed to load executives');
    }
  }, [api]);

  // Initial fetch
  useEffect(() => {
    fetchLeads();
    fetchExecutives();
  }, [fetchLeads, fetchExecutives]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    refreshIntervalRef.current = setInterval(() => {
      fetchLeads(true);
    }, 30000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [fetchLeads]);

  // Manual refresh
  const handleManualRefresh = () => {
    fetchLeads(true);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedLeads(leads.map((l) => l.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId, checked) => {
    if (checked) {
      setSelectedLeads([...selectedLeads, leadId]);
    } else {
      setSelectedLeads(selectedLeads.filter((id) => id !== leadId));
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success('Lead deleted');
      fetchLeads();
    } catch (err) {
      toast.error('Failed to delete lead');
    }
  };

  const fetchDuplicates = async () => {
    setLoadingDuplicates(true);
    try {
      const response = await api.get('/leads/duplicates');
      setDuplicates(response.data.duplicates || []);
      setShowDuplicatesDialog(true);
    } catch (err) {
      toast.error('Failed to fetch duplicates');
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const handleDeleteDuplicate = async (leadId) => {
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success('Duplicate deleted');
      // Refresh duplicates list
      fetchDuplicates();
      fetchLeads();
    } catch (err) {
      toast.error('Failed to delete duplicate');
    }
  };

  const handleAutoAssign = async () => {
    try {
      const response = await api.post('/leads/auto-assign');
      toast.success(response.data.message);
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to auto-assign leads');
    }
  };

  const handleExportCSV = async (detailed = false) => {
    try {
      toast.info('Generating export...');
      
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (sourceFilter && sourceFilter !== 'all') params.append('source', sourceFilter);
      if (assigneeFilter && assigneeFilter !== 'all') params.append('executive_id', assigneeFilter);

      const endpoint = detailed ? '/leads/export/detailed-csv' : '/leads/export/csv';
      const response = await api.get(`${endpoint}?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create and download CSV
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = detailed 
        ? `leads_detailed_export_${new Date().toISOString().split('T')[0]}.csv`
        : `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
      
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename=(.+)/);
        if (matches) filename = matches[1];
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${detailed ? 'detailed' : ''} CSV downloaded`);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('No leads found matching the criteria');
      } else {
        toast.error('Failed to export leads');
      }
    }
  };

  const handleExport = () => handleExportCSV(false);

  const handleExportSelected = async (format = 'csv') => {
    if (selectedLeads.length === 0) return toast.error('Pehle leads select karo!');
    try {
      toast.info(`Exporting ${selectedLeads.length} selected leads...`);
      const response = await api.post(`/leads/export/selected?format=${format}`, 
        { ids: selectedLeads },
        { responseType: 'blob' }
      );
      const mimeType = format === 'excel' 
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv';
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().split('T')[0];
      a.download = format === 'excel' 
        ? `selected_leads_${date}.xlsx` 
        : `selected_leads_${date}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`${selectedLeads.length} leads exported!`);
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handleBulkAssignSuccess = () => {
    setShowBulkAssignDialog(false);
    setSelectedLeads([]);
    fetchLeads();
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="all-leads-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-[500px]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="all-leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
              All Leads
            </h1>
            <p className="text-muted-foreground">
              {total} leads in total
            </p>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="h-8 px-2"
            data-testid="refresh-btn"
            title="Refresh leads"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant="outline" 
            onClick={fetchDuplicates} 
            disabled={loadingDuplicates}
            data-testid="find-duplicates-btn"
          >
            <Copy className="mr-2 h-4 w-4" />
            {loadingDuplicates ? 'Finding...' : 'Find Duplicates'}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-btn">
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Export Options</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleExportCSV(false)} data-testid="export-csv-btn">
                <Download className="mr-2 h-4 w-4" />
                Export to CSV
              </DropdownMenuItem>
              {selectedLeads.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Export Selected ({selectedLeads.length})</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => handleExportSelected('csv')}>
                    <FileDown className="mr-2 h-4 w-4" />
                    Selected → CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExportSelected('excel')}>
                    <FileDown className="mr-2 h-4 w-4" />
                    Selected → Excel
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={() => handleExportCSV(true)} data-testid="export-detailed-btn">
                <Download className="mr-2 h-4 w-4" />
                Export Detailed CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" onClick={() => setShowUploadDialog(true)} data-testid="upload-btn">
            <Upload className="mr-2 h-4 w-4" />
            Upload
          </Button>
          <Button variant="outline" onClick={handleAutoAssign} data-testid="auto-assign-btn">
            <RefreshCcw className="mr-2 h-4 w-4" />
            Auto-Assign
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="add-lead-btn">
            <Plus className="mr-2 h-4 w-4" />
            Add Lead
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, phone, email..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {LEAD_STATUSES.map((status) => (
              <SelectItem key={status} value={status}>{status}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]" data-testid="source-filter">
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {LEAD_SOURCES.map((source) => (
              <SelectItem key={source} value={source}>{source}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={assignmentFilter} onValueChange={(v) => { setAssignmentFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]" data-testid="assignment-filter">
            <SelectValue placeholder="All Leads" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Leads</SelectItem>
            <SelectItem value="assigned">Assigned Leads</SelectItem>
            <SelectItem value="unassigned">Unassigned Leads</SelectItem>
          </SelectContent>
        </Select>
        <Select value={assigneeFilter} onValueChange={(v) => { setAssigneeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]" data-testid="assignee-filter">
            <SelectValue placeholder="All Executives" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Executives</SelectItem>
            {executives.map((exec) => (
              <SelectItem key={exec.id} value={exec.id}>{exec.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions */}
      {selectedLeads.length > 0 && (
        <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
          <span className="text-sm font-medium">
            {selectedLeads.length} lead(s) selected
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowBulkAssignDialog(true)}
            data-testid="bulk-assign-btn"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            Assign
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive"
            onClick={() => {
              if (window.confirm(`Delete ${selectedLeads.length} leads?`)) {
                // Bulk delete not implemented yet
                toast.info('Bulk delete coming soon');
              }
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={selectedLeads.length === leads.length && leads.length > 0}
                  onCheckedChange={handleSelectAll}
                  data-testid="select-all-checkbox"
                />
              </TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>City</TableHead>
              <TableHead>District</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Pin Code</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Assigned To</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                  No leads found
                </TableCell>
              </TableRow>
            ) : (
              leads.map((lead) => (
                <TableRow
                  key={lead.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setSelectedLead(lead)}
                  data-testid={`lead-row-${lead.id}`}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedLeads.includes(lead.id)}
                      onCheckedChange={(checked) => handleSelectLead(lead.id, checked)}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{lead.name}</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div>{lead.mobile}</div>
                      {lead.email && (
                        <div className="text-muted-foreground">{lead.email}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell data-testid={`lead-city-${lead.id}`}>
                    {lead.city || '-'}
                  </TableCell>
                  <TableCell data-testid={`lead-district-${lead.id}`}>
                    {lead.district || '-'}
                  </TableCell>
                  <TableCell data-testid={`lead-state-${lead.id}`}>
                    {lead.state || '-'}
                  </TableCell>
                  <TableCell data-testid={`lead-pincode-${lead.id}`} className="font-mono text-sm">
                    {lead.pinCode || '-'}
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(lead.status)}>
                      {lead.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{lead.leadSource}</TableCell>
                  <TableCell>{lead.assignedExecutiveName || '-'}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {new Date(lead.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`lead-actions-${lead.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setSelectedLead(lead)} data-testid={`view-lead-${lead.id}`}>
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setEditingLead(lead); setShowEditDialog(true); }} data-testid={`edit-lead-${lead.id}`}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit Lead
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => handleDeleteLead(lead.id)}
                          data-testid={`delete-lead-${lead.id}`}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              data-testid="next-page-btn"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Dialogs */}
      <LeadDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={() => { setShowCreateDialog(false); fetchLeads(); }}
        executives={executives}
      />

      <UploadLeadsDialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        onSuccess={() => { setShowUploadDialog(false); fetchLeads(); }}
      />

      <BulkAssignDialog
        open={showBulkAssignDialog}
        onClose={() => setShowBulkAssignDialog(false)}
        onSuccess={handleBulkAssignSuccess}
        selectedLeads={selectedLeads}
        executives={executives}
      />

      {selectedLead && (
        <LeadDetailsDialog
          lead={selectedLead}
          open={!!selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={() => { setSelectedLead(null); fetchLeads(); }}
          executives={executives}
        />
      )}

      {/* Edit Lead Dialog */}
      <EditLeadDialog
        open={showEditDialog}
        onClose={() => { setShowEditDialog(false); setEditingLead(null); }}
        lead={editingLead}
        executives={executives}
        onSuccess={() => { setShowEditDialog(false); setEditingLead(null); fetchLeads(); }}
      />

      {/* Duplicates Dialog */}
      <Dialog open={showDuplicatesDialog} onOpenChange={setShowDuplicatesDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Duplicate Leads Found
            </DialogTitle>
            <DialogDescription>
              These leads have the same mobile number. Review and delete duplicates as needed.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {duplicates.length === 0 ? (
              <div className="text-center py-8">
                <Copy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No Duplicates Found!</p>
                <p className="text-muted-foreground">All leads have unique mobile numbers.</p>
              </div>
            ) : (
              duplicates.map((group, index) => (
                <div key={group.mobile} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="destructive">{group.count} Duplicates</Badge>
                      <span className="font-mono text-sm">{group.mobile}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {group.leads.map((lead, leadIndex) => (
                      <div
                        key={lead.id}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          leadIndex === 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{lead.name}</span>
                            {leadIndex === 0 && (
                              <Badge variant="outline" className="text-green-600 border-green-300">
                                Keep
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            <span>{lead.status}</span>
                            <span className="mx-2">•</span>
                            <span>{lead.leadSource || 'N/A'}</span>
                            <span className="mx-2">•</span>
                            <span>{lead.assignedExecutiveName || 'Unassigned'}</span>
                            <span className="mx-2">•</span>
                            <span>{new Date(lead.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                        {leadIndex !== 0 && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteDuplicate(lead.id)}
                            data-testid={`delete-duplicate-${lead.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllLeadsPage;
